import { css } from "@compiled/react";
import { useIntl } from "react-intl";
import { IntlProvider } from "react-intl-next";

import {
    JiraModalBackground,
    PreviewContainer,
    type Initials
} from "@atlassian/product-setup";

import { getFirstName, getLastName } from "@/components/signup/utils";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { StepContext, StepProps } from "../../../types";

import { ArrowSvg } from "./arrow-svg";

/* istanbul ignore next -- pure CSS declaration */
const previewStyles = css({
    "position": "relative",
    "@media (max-width: 1023px)": {
        display: "none"
    }
});

/* istanbul ignore next -- pure CSS declaration */
const previewCardStyles = css({
    position: "absolute",
    insetInlineStart: "64px",
    insetBlockStart: "24px",
    zIndex: 2
});

/* istanbul ignore next -- pure CSS declaration */
const modalBackgroundStyles = css({
    position: "absolute",
    insetBlockStart: "-64px"
});

/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors -- consumer-side override of @atlassian/product-setup preview styles */
/* istanbul ignore next -- pure CSS declaration */
const previewTableAiOnboardingOverrides = css({
    "& th:nth-child(1) > div": { minWidth: "40px" }
});

/* istanbul ignore next -- pure CSS declaration */
const previewTableOverrides = css({
    "& > div:last-child": {
        overflowY: "auto"
    },
    "& section > div": {
        gap: "16px"
    },
    "& table": {
        tableLayout: "fixed",
        width: "100%"
    },
    "& th:nth-child(1)": { width: "8%" },
    "& th:nth-child(2)": { width: "92%" },
    "& td": {
        fontSize: "12px"
    }
});
/* eslint-enable @atlaskit/ui-styling-standard/no-nested-selectors */

export const Preview = <T extends StepContext>({
    context,
    screenId
}: StepProps<T>) => {
    const { locale } = useIntl();
    const loggedInUser = useLoggedInUser();
    const shouldShowArrow = screenId === "space-details";
    const shouldShowCollaborationCursors =
        screenId === "invite" || screenId === "loading";
    let userInitials = "";
    if (loggedInUser.name) {
        const firstInital = getFirstName(loggedInUser.name).charAt(0);
        const lastInitial = getLastName(loggedInUser.name).charAt(0);
        userInitials = firstInital + lastInitial;
    }

    return (
        <IntlProvider locale={locale}>
            <div css={previewStyles}>
                <div css={modalBackgroundStyles}>
                    <JiraModalBackground />
                </div>
                <div
                    css={[
                        previewCardStyles,
                        previewTableAiOnboardingOverrides,
                        context.previewCardData && previewTableOverrides
                    ]}
                >
                    {shouldShowArrow && (
                        <div
                            css={css({
                                position: "absolute",
                                insetInlineEnd: "90px",
                                insetBlockStart: "-87px"
                            })}
                        >
                            <ArrowSvg />
                        </div>
                    )}
                    <PreviewContainer
                        spaceName={context.spaceName ?? ""}
                        userInitials={userInitials as Initials}
                        previewCardData={
                            context.previewCardData ?? {
                                previewType: context.template
                            }
                        }
                        shouldShowCollaborationCursors={
                            shouldShowCollaborationCursors
                        }
                    />
                </div>
            </div>
        </IntlProvider>
    );
};
